import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Avatar, Stack, Divider, Alert,
  ToggleButtonGroup, ToggleButton, Tooltip, IconButton, LinearProgress,
  Paper, Badge
} from '@mui/material';
import {
  CheckCircle as PresentIcon, Cancel as AbsentIcon,
  Schedule as LateIcon, EventAvailable as ExcusedIcon,
  Save as SaveIcon, CalendarMonth, Group, BarChart,
  Refresh, Today, NavigateBefore, NavigateNext,
  CheckCircleOutline, RadioButtonUnchecked, Download as DownloadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { attendanceAPI, settingsAPI, teachersAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
  present: { label: 'P',    full: 'Present', color: 'success', bg: '#f0fdf4', border: '#86efac', icon: <PresentIcon sx={{ fontSize: 16 }} /> },
  absent:  { label: 'A',    full: 'Absent',  color: 'error',   bg: '#fff2f0', border: '#fca5a5', icon: <AbsentIcon  sx={{ fontSize: 16 }} /> },
  late:    { label: 'L',    full: 'Late',    color: 'warning', bg: '#fffbeb', border: '#fcd34d', icon: <LateIcon    sx={{ fontSize: 16 }} /> },
  excused: { label: 'E',    full: 'Excused', color: 'info',    bg: '#eff6ff', border: '#93c5fd', icon: <ExcusedIcon sx={{ fontSize: 16 }} /> },
};

const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

function StatusButton({ value, onChange, disabled }) {
  const s = STATUS_CONFIG[value] || STATUS_CONFIG.present;
  return (
    <ToggleButtonGroup exclusive value={value} onChange={(_, v) => v && onChange(v)} disabled={disabled} size="small">
      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
        <ToggleButton key={key} value={key}
          sx={{
            px: 1.5, py: 0.4, fontSize: 11, fontWeight: 700,
            color: value === key ? `${cfg.color}.main` : 'text.disabled',
            bgcolor: value === key ? cfg.bg : 'transparent',
            borderColor: value === key ? cfg.border : 'divider',
            '&.Mui-selected': { bgcolor: cfg.bg, color: `${cfg.color}.main` }
          }}>
          {cfg.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// ── Roll Call Tab ─────────────────────────────────────────────────────────────
function RollCallTab({ classes, homeClasses, isClassTeacher, isTeacher }) {
  const { enqueueSnackbar } = useSnackbar();
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(today());
  const [students, setStudents] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setSelectedYear(cur.id);
      }
    }).catch(() => {});
  }, []);

  // Auto-select home class for class_teacher
  useEffect(() => {
    if (isClassTeacher && homeClasses.length > 0) setSelectedClass(homeClasses[0].id);
  }, [isClassTeacher, homeClasses]);

  const availableClasses = isClassTeacher ? homeClasses : classes;

  const loadRollCall = useCallback(async () => {
    if (!selectedClass || !selectedYear || !selectedDate) return;
    setLoading(true); setLoaded(false);
    try {
      const res = await attendanceAPI.getRollCall({
        class_id: selectedClass, academic_year_id: selectedYear, date: selectedDate
      });
      if (res.data.success) {
        setIsSaved(res.data.is_saved);
        if (res.data.is_saved) {
          // Roll call was already taken — show actual saved statuses
          setStudents(res.data.data.map(s => ({ ...s, status: s.status || 'present', notes: s.notes || '' })));
        } else {
          // No roll call yet — load student list but don't default to Present
          setStudents(res.data.data.map(s => ({ ...s, status: null, notes: '' })));
        }
        setLoaded(true);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load roll call', { variant: 'error' });
    } finally { setLoading(false); }
  }, [selectedClass, selectedYear, selectedDate, enqueueSnackbar]);

  // Auto-load whenever class, year or date changes and all are set
  useEffect(() => {
    if (selectedClass && selectedYear && selectedDate) {
      loadRollCall();
    }
  }, [selectedClass, selectedYear, selectedDate]); // eslint-disable-line

  const markAll = (status) => setStudents(prev => prev.map(s => ({ ...s, status })));

  const saveRollCall = async () => {
    if (!students.length) return;
    // Default any null statuses to 'present' before saving
    const recordsToSave = students.map(s => ({
      student_id: s.id,
      status: s.status || 'present',
      notes: s.notes || ''
    }));
    setSaving(true);
    try {
      await attendanceAPI.saveRollCall({
        class_id: selectedClass, academic_year_id: selectedYear, date: selectedDate,
        records: recordsToSave
      });
      // Update local state to reflect saved statuses
      setStudents(prev => prev.map(s => ({ ...s, status: s.status || 'present' })));
      enqueueSnackbar('Roll call saved successfully', { variant: 'success' });
      setIsSaved(true);
    } catch { enqueueSnackbar('Failed to save roll call', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const shiftDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
    setLoaded(false);
  };

  const counts = students.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
  const presentCount = (counts.present || 0) + (counts.late || 0) + (counts.excused || 0);
  const attendancePct = students.length > 0 ? Math.round((presentCount / students.length) * 100) : 0;

  return (
    <Box>
      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select size="small" fullWidth label="Academic Year" value={selectedYear}
              onChange={e => { setSelectedYear(e.target.value); setLoaded(false); }}>
              {(academicYears.some(y => y.is_current) ? academicYears.filter(y => y.is_current) : academicYears).map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term} (Current)</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select size="small" fullWidth label="Class" value={selectedClass}
              onChange={e => { setSelectedClass(e.target.value); setLoaded(false); }}
              disabled={isClassTeacher && homeClasses.length === 1}>
              <MenuItem value="">Select class…</MenuItem>
              {availableClasses.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Tooltip title="Previous day"><IconButton size="small" onClick={() => shiftDate(-1)}><NavigateBefore /></IconButton></Tooltip>
              <TextField type="date" size="small" value={selectedDate}
                onChange={e => { setSelectedDate(e.target.value); setLoaded(false); }}
                InputLabelProps={{ shrink: true }} label="Date" sx={{ flex: 1 }} />
              <Tooltip title="Next day"><IconButton size="small" onClick={() => shiftDate(1)} disabled={selectedDate >= today()}><NavigateNext /></IconButton></Tooltip>
              <Tooltip title="Today"><IconButton size="small" onClick={() => { setSelectedDate(today()); setLoaded(false); }}><Today fontSize="small" /></IconButton></Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="outlined" fullWidth startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={loadRollCall} disabled={!selectedClass || !selectedYear || loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Roll call table */}
      {loaded && (
        <Box>
          {/* Not yet taken — show prompt */}
          {!isSaved && (
            <Alert severity="warning" sx={{ mb: 2 }}
              action={
                <Button color="warning" variant="contained" size="small"
                  onClick={() => setStudents(prev => prev.map(s => ({ ...s, status: 'present' })))}>
                  Start Roll Call (All Present)
                </Button>
              }>
              <strong>Roll call not yet taken</strong> for {fmtDate(selectedDate)}. Click "Start Roll Call" to begin, or set each student's status individually below.
            </Alert>
          )}

          {/* Stats + actions bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {fmtDate(selectedDate)}
                {isSaved && <Chip label="✓ Saved" color="success" size="small" sx={{ ml: 1, fontSize: 10 }} />}
                {!isSaved && students.some(s => s.status) && <Chip label="Unsaved changes" color="warning" size="small" sx={{ ml: 1, fontSize: 10 }} />}
              </Typography>
              {students.some(s => s.status) && (
                <Stack direction="row" spacing={1} mt={0.5}>
                  {Object.entries(counts).filter(([s]) => s !== 'null').map(([status, count]) => (
                    <Chip key={status} icon={STATUS_CONFIG[status]?.icon} label={`${count} ${STATUS_CONFIG[status]?.full}`}
                      size="small" color={STATUS_CONFIG[status]?.color} variant="outlined" sx={{ fontSize: 11 }} />
                  ))}
                  <Chip label={`${attendancePct}% attendance`} size="small"
                    color={attendancePct >= 80 ? 'success' : attendancePct >= 60 ? 'warning' : 'error'}
                    sx={{ fontWeight: 700, fontSize: 11 }} />
                </Stack>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" color="success" onClick={() => markAll('present')}>All Present</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => markAll('absent')}>All Absent</Button>
              <Button variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={saveRollCall} disabled={saving || !students.some(s => s.status)}>
                Save Roll Call
              </Button>
            </Stack>
          </Box>

          {/* Progress bar — only show when statuses set */}
          {students.some(s => s.status) && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={attendancePct}
                color={attendancePct >= 80 ? 'success' : attendancePct >= 60 ? 'warning' : 'error'}
                sx={{ height: 6, borderRadius: 3 }} />
            </Box>
          )}

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, width: 50, fontSize: 11 }}>#</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Student</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Admission</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student, idx) => {
                    const cfg = student.status ? (STATUS_CONFIG[student.status] || STATUS_CONFIG.present) : null;
                    return (
                      <TableRow key={student.id} hover
                        sx={{ bgcolor: student.status && student.status !== 'present' ? cfg.bg : 'white' }}>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{idx + 1}</TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: 11,
                              bgcolor: !student.status ? 'grey.400' : student.status === 'absent' ? 'error.main' : student.status === 'late' ? 'warning.main' : 'primary.main' }}>
                              {student.first_name?.[0]}{student.last_name?.[0]}
                            </Avatar>
                            <Typography fontSize={13} fontWeight={500}>
                              {student.last_name}, {student.first_name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{student.admission_number}</TableCell>
                        <TableCell align="center">
                          <StatusButton value={student.status || ''}
                            onChange={(v) => setStudents(prev => prev.map(s => s.id === student.id ? { ...s, status: v } : s))} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="Optional note…" value={student.notes}
                            onChange={e => setStudents(prev => prev.map(s => s.id === student.id ? { ...s, notes: e.target.value } : s))}
                            sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
            <strong>P</strong> = Present &nbsp;|&nbsp; <strong>A</strong> = Absent &nbsp;|&nbsp;
            <strong>L</strong> = Late (counted as present for attendance %) &nbsp;|&nbsp;
            <strong>E</strong> = Excused (counted as present for attendance %)
          </Alert>
        </Box>
      )}

      {!loaded && !loading && selectedClass && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <CalendarMonth sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Select a class and date — students will load automatically</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Summary Tab ───────────────────────────────────────────────────────────────
function SummaryTab({ classes, homeClasses, isClassTeacher }) {
  const { enqueueSnackbar } = useSnackbar();
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [summary, setSummary]   = useState([]);
  const [dates, setDates]       = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => {
      if (r.data.success) {
        setAcademicYears(r.data.data);
        const cur = r.data.data.find(y => y.is_current);
        if (cur) setSelectedYear(cur.id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isClassTeacher && homeClasses.length > 0) setSelectedClass(homeClasses[0].id);
  }, [isClassTeacher, homeClasses]);

  const load = async () => {
    if (!selectedClass || !selectedYear) return;
    setLoading(true);
    try {
      const res = await attendanceAPI.getClassSummary({
        class_id: selectedClass, academic_year_id: selectedYear,
        date_from: dateFrom || undefined, date_to: dateTo || undefined
      });
      if (res.data.success) { setSummary(res.data.data); setDates(res.data.dates || []); }
    } catch { enqueueSnackbar('Failed to load summary', { variant: 'error' }); }
    finally { setLoading(false); }
  };

  const availableClasses = isClassTeacher ? homeClasses : classes;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select size="small" fullWidth label="Academic Year" value={selectedYear}
              onChange={e => setSelectedYear(e.target.value)}>
              {academicYears.map(y => (
                <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select size="small" fullWidth label="Class" value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)} disabled={isClassTeacher && homeClasses.length === 1}>
              <MenuItem value="">Select…</MenuItem>
              {availableClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField type="date" size="small" fullWidth label="From" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField type="date" size="small" fullWidth label="To" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button variant="contained" fullWidth startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <BarChart />}
              onClick={load} disabled={!selectedClass || !selectedYear || loading}>
              Generate Summary
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {summary.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {dates.length} school days recorded
            </Typography>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
              onClick={() => {
                const headers = ['Student', 'Admission No', 'Days Recorded', 'Present', 'Absent', 'Late', 'Excused', 'Attendance %'];
                const rows = summary.map(st => [
                  `${st.last_name}, ${st.first_name}`,
                  st.admission_number,
                  st.total_days || 0,
                  st.present || 0,
                  st.absent || 0,
                  st.late || 0,
                  st.excused || 0,
                  st.attendance_pct != null ? `${st.attendance_pct}%` : '—'
                ]);
                const cls = availableClasses.find(c => c.id == selectedClass);
                const clsName = cls ? `${cls.name}${cls.stream ? '_' + cls.stream : ''}` : 'class';
                exportCSV(`attendance_summary_${clsName}`, headers, rows);
              }}>
              Export CSV
            </Button>
          </Box>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>Student</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Days</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Present</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Absent</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Late</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11 }} align="center">Excused</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: 11, minWidth: 120 }}>Attendance %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map((st, idx) => {
                    const pct = st.attendance_pct || 0;
                    const color = pct >= 80 ? 'success' : pct >= 60 ? 'warning' : 'error';
                    return (
                      <TableRow key={st.id} hover sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 26, height: 26, fontSize: 10, bgcolor: `${color}.main` }}>
                              {st.first_name?.[0]}{st.last_name?.[0]}
                            </Avatar>
                            <Typography fontSize={13}>{st.last_name}, {st.first_name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 12, fontWeight: 600 }}>{st.total_days || 0}</TableCell>
                        <TableCell align="center"><Chip label={st.present || 0} size="small" color="success" variant="outlined" sx={{ fontSize: 11, fontWeight: 700 }} /></TableCell>
                        <TableCell align="center"><Chip label={st.absent || 0} size="small" color="error" variant="outlined" sx={{ fontSize: 11, fontWeight: 700 }} /></TableCell>
                        <TableCell align="center"><Chip label={st.late || 0} size="small" color="warning" variant="outlined" sx={{ fontSize: 11, fontWeight: 700 }} /></TableCell>
                        <TableCell align="center"><Chip label={st.excused || 0} size="small" color="info" variant="outlined" sx={{ fontSize: 11, fontWeight: 700 }} /></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={color}
                              sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                            <Typography fontSize={12} fontWeight={700} color={`${color}.main`} sx={{ minWidth: 40 }}>
                              {pct != null ? `${pct}%` : '—'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Alert severity={summary.some(s => s.attendance_pct < 75) ? 'warning' : 'success'} sx={{ mt: 2 }}>
            {summary.filter(s => s.attendance_pct != null && s.attendance_pct < 75).length > 0
              ? `⚠️ ${summary.filter(s => s.attendance_pct != null && s.attendance_pct < 75).length} student(s) below 75% attendance threshold — may affect passing criteria evaluation.`
              : '✅ All students meet the 75% minimum attendance threshold.'}
          </Alert>
        </>
      )}

      {summary.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <BarChart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Select a class and click Generate Summary</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Attendance() {
  const { user } = useAuth();
  const isClassTeacher = user?.role === 'class_teacher';
  const isTeacher = user?.role === 'teacher';

  const [tab, setTab] = useState(0);
  const [classes, setClasses] = useState([]);
  const [homeClasses, setHomeClasses] = useState([]);

  useEffect(() => {
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    if (user?.teacher_id) {
      teachersAPI.getMyHomeClasses().then(r => { if (r.data.success) setHomeClasses(r.data.data); }).catch(() => {});
    }
  }, [user]);

  // Only class_teacher and admin can mark attendance
  if (user && user.role !== 'admin' && user.role !== 'class_teacher') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CalendarMonth sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Access Restricted</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Attendance can only be marked by Class Teachers. Contact your administrator if you need access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth color="primary" /> Attendance Tracking
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Daily roll call — used in passing criteria evaluation
        </Typography>
      </Box>

      {/* Tab bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        {['Roll Call', 'Attendance Summary'].map((label, i) => (
          <Button key={i} variant={tab === i ? 'contained' : 'outlined'}
            onClick={() => setTab(i)} size="small"
            sx={{ borderRadius: 5, px: 2.5 }}>
            {label}
          </Button>
        ))}
      </Stack>

      {tab === 0 && (
        <RollCallTab classes={classes} homeClasses={homeClasses}
          isClassTeacher={isClassTeacher} isTeacher={isTeacher} />
      )}
      {tab === 1 && (
        <SummaryTab classes={classes} homeClasses={homeClasses}
          isClassTeacher={isClassTeacher} />
      )}
    </Box>
  );
}
